import Flex from "common/Boxes/Flex";
import { toFixed } from "@shared/utils/numbers";
import { InitialPrice } from "features/settings/common/styles";
import styled, { css, useTheme } from "styled-components";
import { formatMoney } from "features/settings/Subscription/NewLook/formatMoney.ts";
import Text from "common/Typography/Text";
import { CSSProperties, ReactNode } from "react";
import useMedia from "utils/hooks/useMedia.ts";
import { MOBILE_SEARCH } from "common/constants.ts";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import Tooltip from "antd/es/tooltip";

interface SummaryRowProps {
  label?: string | React.JSX.Element;
  initialPrice?: number;
  price: number;
  suffix?: string;
  style?: CSSProperties;
  tooltipContent?: ReactNode;
}

const SummaryRow = ({
  label,
  initialPrice,
  price,
  suffix,
  style,
  tooltipContent,
}: SummaryRowProps) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { isNewLook } = useTheme();
  // The new look always shows cents on fractional prices.
  const money = (value: number) =>
    isNewLook ? formatMoney(value) : toFixed(value, 2);

  return (
    <Flex style={{ marginBottom: isMobile ? 12 : 16, ...style }}>
      <RowLabel as="div">{label}</RowLabel>
      <Flex>
        {initialPrice && initialPrice !== price && (
          <InitialPrice>${money(initialPrice)}</InitialPrice>
        )}
        {tooltipContent ? (
          <Tooltip title={tooltipContent}>
            <PriceWithHint>
              ${money(price)}
              {suffix} <InfoIcon />
            </PriceWithHint>
          </Tooltip>
        ) : (
          <Price>
            ${money(price)}
            {suffix}
          </Price>
        )}
      </Flex>
    </Flex>
  );
};

export default SummaryRow;

const RowLabel = styled(Text)`
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  color: ${(p) => p.theme.colors.mediumGrey};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        color: ${p.theme.colors.secondaryText};
        font-size: 13.5px;
        font-weight: ${p.theme.typography.fontWeights.NORMAL};
      }
    `}
`;
const Price = styled(Text)`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        color: ${p.theme.colors.titleColor};
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
        font-variant-numeric: tabular-nums;
      }
    `}
`;
const PriceWithHint = styled(Text)`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  cursor: help;
  border-bottom: 1px dashed currentColor;
  display: inline;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
        font-variant-numeric: tabular-nums;
      }
    `}
`;
const InfoIcon = styled(InfoCircleOutlined)`
  font-size: 12px;
  color: inherit;
  margin-left: 4px;
`;
